/**
 * @file   首页轮播-第 2 屏：文物易绘
 * @create 2026/9/16
 * @description 参考原型「瑞鹿云-门户网站原型/index.html」的文物易绘屏实现：
 *              背景动效与第 1 屏（智慧文旅）一致，复用同一个背景视频（仅当前屏播放）；
 *              左侧文案（AI 图像生成技术 / 行业标准 / 免费试用 / 三项能力）+ 右侧陶罐线图；
 *              免费试用：未登录跳转本站原有注册页，已登录免登录直接进入文物易绘
 */
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import pottery from '../../assets/home/pottery.svg'
import referenceBackground from '../../assets/home/reference-background.png'
import shuwenLogo from '../../assets/home/shuwen-tech-white.png'
import heritageLogo from '../../assets/home/zhejiang-ai-cultural-heritage-logo.jpg'
import { localDS } from '../../storage'

/** 图标路径（取自原型 SVG sprite） */
const ICON_PATHS = {
    arrow: 'M4 12h15m-6-6 6 6-6 6'
};

/**
 * @description 通用线性图标
 * @param {String} type 图标类型
 * @param {Number} strokeWidth 线宽
 */
const Icon = ({ type, strokeWidth = 1.35 }) => (
    <svg className='icon' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={strokeWidth} strokeLinecap='round' strokeLinejoin='round'>
        <path d={ICON_PATHS[type]} />
    </svg>
);

/**
 * @description 文物易绘轮播屏
 * @param {Boolean} active 是否为当前展示屏
 */
export const ArchaeologySlide = ({ active }) => {
    // 背景视频（与第 1 屏同一个视频，保证动效一致）
    const videoRef = useRef(null);

    // 背景动效与第 1 屏一致：仅当前屏播放，切走后暂停，避免两个视频同时解码
    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        if (active) {
            const playPromise = video.play();
            if (playPromise && playPromise.catch) {
                playPromise.catch(() => { });
            }
        } else {
            video.pause();
        }
    }, [active]);

    /**
     * 免费试用点击：已登录时免登录直接进入文物易绘（与产品列表红按钮 redTryHandler 口径一致），
     * 统一携带 authorizationc（门户不再使用 authorizationu 跳转）；
     * 未登录时保持原逻辑，走 Link 默认跳转注册页
     */
    const onTrialClick = (event) => {
        const saasToken = localDS.get('saasToken');
        if (!saasToken) {
            console.log('[archaeology] 免费试用-未登录，跳转注册页: ' + JSON.stringify({ to: '/register?from=red' }));
            return;
        }
        event.preventDefault();
        const targetLink = window.RED_LOGIN + `?authorizationc=${saasToken}`;
        console.log('[archaeology] 免费试用-已登录，跳转文物易绘: ' + JSON.stringify({ targetLink: targetLink }));
        window.location.href = targetLink;
    };

    return (
        <div className='archaeology-slide'>
            {/* 背景动效：与第 1 屏同一个视频，海报为同版静态背景图（视频未就绪时兜底） */}
            <video
                className='archaeology-video'
                ref={videoRef}
                src='./video/home.mp4'
                poster={referenceBackground}
                muted
                loop='loop'
                playsInline
            />
            {/* 空白区域可点击：进入文物易绘详情页（文案区在其上层，不遮挡文案内的按钮） */}
            <Link className='archaeology-page-link' to='/ArchaeologyLineDrawing' aria-label='进入文物易绘详情页' />
            <div className='archaeology-copy'>
                <h2>文物易绘</h2>
                <p className='archaeology-description'>
                    <span className='description-kicker'>基于 AI 图像生成技术</span>
                    <span className='description-value'>一站式完成文物线图生成、编辑、导出与管理</span>
                </p>
                <p className='standard'>WW/T 0035—2012《田野考古制图》行业标准</p>
                <div className='archaeology-actions'>
                    <div className='archaeology-cta-row'>
                        <Link className='trial-button' to='/register?from=red' onClick={onTrialClick}>
                            免费试用
                            <Icon type='arrow' strokeWidth={1.8} />
                        </Link>
                        {/* 查看定价：跳转详情页并定位到产品定价区块 */}
                        <Link className='price-button' to='/ArchaeologyLineDrawing#pricing'>
                            查看定价
                        </Link>
                        {/* 了解更多：进入文物易绘详情页 */}
                        <Link className='more-button' to='/ArchaeologyLineDrawing'>
                            了解更多
                        </Link>
                    </div>
                    {/* 合作方标识：数文科技 + 浙大城市学院（与原型一致，纯展示） */}
                    <div className='partner-signature'>
                        <div className='partner-logos' aria-label='数文科技与浙江省人工智能与文化遗产国际科技合作基地'>
                            <img className='shuwen-logo' src={shuwenLogo} alt='数文科技' />
                            <span className='partner-divider' aria-hidden='true' />
                            <img className='hcu-logo' src={heritageLogo} alt='浙江省人工智能与文化遗产国际科技合作基地' />
                        </div>
                        <small className='partner-label'>联合发布</small>
                    </div>
                </div>
            </div>
            <div className='archaeology-art'>
                <img src={pottery} alt='陶罐文物线图，左侧剖面、右侧外形。' />
            </div>
        </div>
    )
}